import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Tag,
  Plus,
  Pencil,
  Trash2,
  Percent,
  DollarSign,
  Calendar,
  Copy,
  CheckCircle,
  XCircle,
  Loader2,
  Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDiscountCodes = () => {
  const { token, API: authAPI } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showExpired, setShowExpired] = useState(false);

  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    description: "",
    applicable_programs: [],
    max_uses: "",
    expires_at: "",
    min_purchase_amount: "",
    is_active: true,
  });

  const programs = [
    { key: "foundations_first", name: "Foundations First" },
    { key: "carerevive", name: "CareRevive" },
    { key: "frontline_concierge", name: "Frontline Concierge" },
  ];

  useEffect(() => {
    fetchCodes();
  }, [showExpired]);

  const fetchCodes = async () => {
    try {
      const response = await fetch(
        `${API}/admin/discounts?include_expired=${showExpired}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCodes(data);
      }
    } catch (error) {
      toast.error("Failed to load discount codes");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProgramToggle = (programKey) => {
    setFormData((prev) => {
      const current = prev.applicable_programs || [];
      if (current.includes(programKey)) {
        return { ...prev, applicable_programs: current.filter((p) => p !== programKey) };
      } else {
        return { ...prev, applicable_programs: [...current, programKey] };
      }
    });
  };

  const openEditor = (code = null) => {
    if (code) {
      setEditingCode(code);
      setFormData({
        code: code.code,
        discount_type: code.discount_type,
        discount_value: code.discount_value.toString(),
        description: code.description || "",
        applicable_programs: code.applicable_programs || [],
        max_uses: code.max_uses?.toString() || "",
        expires_at: code.expires_at ? code.expires_at.split("T")[0] : "",
        min_purchase_amount: code.min_purchase_amount?.toString() || "",
        is_active: code.is_active !== false,
      });
    } else {
      setEditingCode(null);
      setFormData({
        code: "",
        discount_type: "percentage",
        discount_value: "",
        description: "",
        applicable_programs: [],
        max_uses: "",
        expires_at: "",
        min_purchase_amount: "",
        is_active: true,
      });
    }
    setShowEditor(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.discount_value) {
      toast.error("Please fill in code and discount value");
      return;
    }

    setSaving(true);

    try {
      const params = new URLSearchParams({
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
        description: formData.description,
        applicable_programs: formData.applicable_programs.join(","),
        is_active: formData.is_active.toString(),
      });

      if (formData.max_uses) params.append("max_uses", formData.max_uses);
      if (formData.expires_at) params.append("expires_at", new Date(formData.expires_at).toISOString());
      if (formData.min_purchase_amount) params.append("min_purchase_amount", formData.min_purchase_amount);

      const url = editingCode
        ? `${API}/admin/discounts/${editingCode.discount_id}?${params.toString()}`
        : `${API}/admin/discounts?${params.toString()}`;

      const response = await fetch(url, {
        method: editingCode ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save discount code");
      }

      toast.success(editingCode ? "Discount code updated!" : "Discount code created!");
      setShowEditor(false);
      fetchCodes();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (discountId) => {
    if (!confirm("Are you sure you want to delete this discount code?")) return;

    try {
      const response = await fetch(`${API}/admin/discounts/${discountId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to delete discount code");

      toast.success("Discount code deleted!");
      fetchCodes();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleActive = async (code) => {
    try {
      const params = new URLSearchParams({ is_active: (!code.is_active).toString() });
      
      const response = await fetch(
        `${API}/admin/discounts/${code.discount_id}?${params.toString()}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to update discount code");

      toast.success(code.is_active ? "Discount code deactivated" : "Discount code activated");
      fetchCodes();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard!");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No expiry";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isExpired = (code) => {
    if (!code.expires_at) return false;
    return new Date(code.expires_at) < new Date();
  };

  const isMaxedOut = (code) => {
    if (!code.max_uses) return false;
    return code.times_used >= code.max_uses;
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <Badge variant="outline" className="text-gray-500">Inactive</Badge>;
    }
    if (isExpired(code)) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (isMaxedOut(code)) {
      return <Badge variant="outline" className="text-orange-600 border-orange-300">Maxed Out</Badge>;
    }
    return <Badge className="bg-green-600">Active</Badge>;
  };

  const filteredCodes = codes.filter((code) =>
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const activeCodes = codes.filter((c) => c.is_active && !isExpired(c) && !isMaxedOut(c));
  const totalRedemptions = codes.reduce((sum, c) => sum + (c.times_used || 0), 0);

  return (
    <AdminLayout activeNav="discounts">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-rnb-navy">Discount Codes</h1>
            <p className="text-sm text-rnb-gray">Create and manage promotional codes for signature programs</p>
          </div>
          <Button onClick={() => openEditor()} data-testid="create-discount-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Discount Code
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Tag className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">{activeCodes.length}</p>
                  <p className="text-sm text-rnb-gray">Active Codes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">{totalRedemptions}</p>
                  <p className="text-sm text-rnb-gray">Total Redemptions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Percent className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-rnb-navy">{codes.length}</p>
                  <p className="text-sm text-rnb-gray">Total Codes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-rnb-gray" />
                <Input
                  placeholder="Search codes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={showExpired}
                  onCheckedChange={setShowExpired}
                />
                Show expired codes
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Codes Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Discount Codes</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-rnb-primary" />
              </div>
            ) : filteredCodes.length === 0 ? (
              <div className="text-center py-12">
                <Tag className="h-12 w-12 text-rnb-gray/30 mx-auto mb-4" />
                <p className="text-rnb-gray">No discount codes found</p>
                <Button className="mt-4" onClick={() => openEditor()}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Code
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Programs</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((code) => (
                    <TableRow key={code.discount_id} data-testid={`discount-${code.discount_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-rnb-navy">{code.code}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyCode(code.code)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        {code.description && (
                          <p className="text-xs text-rnb-gray mt-1">{code.description}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {code.discount_type === "percentage" ? (
                            <>
                              <Percent className="h-4 w-4 text-rnb-gray" />
                              <span className="font-semibold">{code.discount_value}%</span>
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4 text-rnb-gray" />
                              <span className="font-semibold">{code.discount_value}</span>
                            </>
                          )}
                        </div>
                        {code.min_purchase_amount && (
                          <p className="text-xs text-rnb-gray">Min ${code.min_purchase_amount}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {code.applicable_programs?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {code.applicable_programs.map((p) => (
                              <Badge key={p} variant="outline" className="text-xs">
                                {programs.find((pr) => pr.key === p)?.name || p}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-rnb-gray">All programs</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{code.times_used || 0}</span>
                        {code.max_uses && (
                          <span className="text-rnb-gray">/{code.max_uses}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(code.expires_at)}
                      </TableCell>
                      <TableCell>{getStatusBadge(code)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(code)}
                            title={code.is_active ? "Deactivate" : "Activate"}
                          >
                            {code.is_active ? (
                              <XCircle className="h-4 w-4 text-orange-500" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditor(code)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(code.discount_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingCode ? "Edit Discount Code" : "Create Discount Code"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                    placeholder="SAVE20"
                    className="font-mono uppercase"
                    data-testid="discount-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount Type *</Label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(v) => handleChange("discount_type", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    Discount Value * {formData.discount_type === "percentage" ? "(%)" : "($)"}
                  </Label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => handleChange("discount_value", e.target.value)}
                    placeholder={formData.discount_type === "percentage" ? "20" : "50"}
                    min="0"
                    max={formData.discount_type === "percentage" ? "100" : undefined}
                    data-testid="discount-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Uses (optional)</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => handleChange("max_uses", e.target.value)}
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Holiday promotion - 20% off"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Expires On (optional)</Label>
                  <Input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => handleChange("expires_at", e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min Purchase ($)</Label>
                  <Input
                    type="number"
                    value={formData.min_purchase_amount}
                    onChange={(e) => handleChange("min_purchase_amount", e.target.value)}
                    placeholder="No minimum"
                    min="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Applicable Programs</Label>
                <p className="text-xs text-rnb-gray mb-2">Leave all unchecked for all programs</p>
                <div className="grid grid-cols-3 gap-2">
                  {programs.map((program) => (
                    <label
                      key={program.key}
                      className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50"
                    >
                      <Checkbox
                        checked={formData.applicable_programs.includes(program.key)}
                        onCheckedChange={() => handleProgramToggle(program.key)}
                      />
                      <span className="text-sm">{program.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange("is_active", checked)}
                  id="is_active"
                />
                <Label htmlFor="is_active">Code is active</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-discount-btn">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingCode ? (
                    "Update Code"
                  ) : (
                    "Create Code"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminDiscountCodes;
